import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "@/components/Layout.jsx";
import ProtectedRoute from "@/components/ProtectedRoute.jsx";
import { AuthProvider } from "@/context/AuthContext.jsx";
import Login from "@/pages/Login.jsx";
import Dashboard from "@/pages/Dashboard.jsx";
import "@/App.css";

// Code-Splitting: Lazy load große Komponenten
const Users = lazy(() => import("@/pages/Users.jsx"));
const BeckenDetail = lazy(() => import("@/pages/BeckenDetail.jsx"));
const AnlagenDetail = lazy(() => import("@/pages/AnlagenDetail.jsx"));
const ImportExcel = lazy(() => import("@/pages/ImportExcel.jsx"));
const Settings = lazy(() => import("@/pages/Settings.jsx"));
const BauhofManagement = lazy(() => import("@/pages/BauhofManagement.jsx"));
const MobilePhotoUpload = lazy(() => import("@/pages/MobilePhotoUpload.jsx"));
const AuditLog = lazy(() => import("@/pages/AuditLog.jsx"));
const UsedBecken = lazy(() => import("@/pages/UsedBecken.jsx"));
const Statistics = lazy(() => import("@/pages/Statistics.jsx"));
const DataCheck = lazy(() => import("@/pages/DataCheck.jsx"));
const NeueBilder = lazy(() => import("@/pages/NeueBilder.jsx"));
const Checkout = lazy(() => import("@/pages/Checkout.jsx"));
const Wartungen = lazy(() => import("@/pages/Wartungen.jsx"));
const WartungMap = lazy(() => import("@/pages/WartungMap.jsx"));
const Ruecklieferungen = lazy(() => import("@/pages/Ruecklieferungen.jsx"));
const Lebenslauf = lazy(() => import("@/pages/Lebenslauf.jsx"));
const Batchbearbeitung = lazy(() => import("@/pages/Batchbearbeitung.jsx"));
const Reisen = lazy(() => import("@/pages/Reisen.jsx"));
const ReisenStatistik = lazy(() => import("@/pages/ReisenStatistik.jsx"));
const MediaVerwaltung = lazy(() => import("@/pages/MediaVerwaltung.jsx"));
const Urlaubsplaner = lazy(() => import("@/pages/Urlaubsplaner.jsx"));
const ReportingManagement = lazy(() => import("@/pages/ReportingManagement.jsx"));
const CadUploaderPortal = lazy(() => import("@/pages/CadUploaderPortal.jsx"));
const Anfragen = lazy(() => import("@/pages/Anfragen.jsx"));
const Kalkulation = lazy(() => import("@/pages/Kalkulation.jsx"));

// Loading Fallback Komponente
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Lädt...</p>
    </div>
  </div>
);

function App() {
  // Custom Domain Routing: absetzbecken-gebraucht.de → /used (mit und ohne www)
  const isCustomDomain = window.location.hostname.endsWith('absetzbecken-gebraucht.de');
  
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/login" element={<Layout><Login /></Layout>} />
              <Route path="/cad-portal" element={<ProtectedRoute allow={["cad_uploader", "superadmin", "backoffice"]}><CadUploaderPortal /></ProtectedRoute>} />
              {/* Custom Domain: Show /used content on root path */}
              <Route 
                path="/" 
                element={
                  isCustomDomain 
                    ? <UsedBecken /> 
                    : <ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Dashboard /></Layout></ProtectedRoute>
                } 
              />
              <Route path="/users" element={<ProtectedRoute allow={["superadmin"]}><Layout><Users /></Layout></ProtectedRoute>} />
              <Route path="/import" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><ImportExcel /></Layout></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Settings /></Layout></ProtectedRoute>} />
              <Route path="/bauhof" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><BauhofManagement /></Layout></ProtectedRoute>} />
              <Route path="/audit-log" element={<ProtectedRoute allow={["superadmin"]}><Layout><AuditLog /></Layout></ProtectedRoute>} />
              <Route path="/mobile-upload" element={<ProtectedRoute><MobilePhotoUpload /></ProtectedRoute>} />
              <Route path="/used" element={<UsedBecken />} />
              <Route path="/statistics" element={<ProtectedRoute allow={["superadmin"]}><Layout><Statistics /></Layout></ProtectedRoute>} />
              <Route path="/data-check" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><DataCheck /></Layout></ProtectedRoute>} />
              <Route path="/neue-bilder" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><NeueBilder /></Layout></ProtectedRoute>} />
              <Route path="/checkout" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Checkout /></Layout></ProtectedRoute>} />
              <Route path="/checkouts" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Checkout /></Layout></ProtectedRoute>} />
              <Route path="/wartungen" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Wartungen /></Layout></ProtectedRoute>} />
              <Route path="/wartungen/karte" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><WartungMap /></Layout></ProtectedRoute>} />
              <Route path="/ruecklieferungen" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Ruecklieferungen /></Layout></ProtectedRoute>} />
              <Route path="/lebenslauf" element={<ProtectedRoute allow={["superadmin", "backoffice", "user"]}><Layout><Lebenslauf /></Layout></ProtectedRoute>} />
              <Route path="/batch-bearbeitung" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Batchbearbeitung /></Layout></ProtectedRoute>} />
              <Route path="/reisen" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Reisen /></Layout></ProtectedRoute>} />
              <Route path="/reisen-statistik" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><ReisenStatistik /></Layout></ProtectedRoute>} />
              <Route path="/media-verwaltung" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><MediaVerwaltung /></Layout></ProtectedRoute>} />
              <Route path="/urlaubsplaner" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Urlaubsplaner /></Layout></ProtectedRoute>} />
              <Route path="/reporting" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><ReportingManagement /></Layout></ProtectedRoute>} />
              <Route path="/anfragen" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Anfragen /></Layout></ProtectedRoute>} />
              <Route path="/kalkulation" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><Kalkulation /></Layout></ProtectedRoute>} />
              <Route path="/becken/:id" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><BeckenDetail /></Layout></ProtectedRoute>} />
              <Route path="/anlagen/:id" element={<ProtectedRoute allow={["superadmin", "backoffice"]}><Layout><AnlagenDetail /></Layout></ProtectedRoute>} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
